'use client';

import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, CircleQuestionMark } from 'lucide-react';
import type { WorkloadMetricsInterval } from '@/lib/urm-api';
import { MetricsLoadingOverlay } from '@/components/metrics-loading-overlay';
import { cn } from '@/lib/utils';
import {
  buildWorkloadLatencySummaries,
  type WorkloadLatencySummary,
} from '../latency.logic';
import {
  DEFAULT_WORKLOAD_INSIGHT_STATE,
  GROUPING_DIMENSIONS,
  type GroupingDimension,
  type RequestLatencyMetric,
} from '../types';

const AVAILABLE_DIMENSIONS = GROUPING_DIMENSIONS.filter(
  (dimension) => dimension.key !== 'username',
);

const REQUEST_LATENCY_METRICS: Array<{ key: RequestLatencyMetric; label: string }> = [
  { key: 'totalRequests', label: 'Total requests' },
  { key: 'processingSharePercent', label: 'Processing load' },
  { key: 'averageDurationMs', label: 'Average' },
  { key: 'histogramP50Ms', label: 'Approx. p50' },
  { key: 'histogramP95Ms', label: 'Approx. p95' },
  { key: 'histogramP99Ms', label: 'Approx. p99' },
  { key: 'slowerThan250Percent', label: '≥250 ms' },
  { key: 'slowerThan1000Percent', label: '≥1 second' },
];

function metricValue(
  summary: WorkloadLatencySummary,
  metric: RequestLatencyMetric,
): number | null {
  return summary[metric];
}

function formatLatency(value: number): string {
  return value >= 1_000
    ? `${(value / 1_000).toLocaleString(undefined, { maximumFractionDigits: 2 })} s`
    : `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ms`;
}

function formatMetricValue(
  summary: WorkloadLatencySummary,
  metric: RequestLatencyMetric,
): string {
  const value = metricValue(summary, metric);
  if (value === null) return '—';
  if (metric === 'totalRequests') return value.toLocaleString();
  if (metric === 'processingSharePercent'
    || metric === 'slowerThan250Percent'
    || metric === 'slowerThan1000Percent') {
    const formatted = value > 0 && value < 0.01
      ? '<0.01'
      : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return `${formatted}%`;
  }
  return formatLatency(value);
}

export function WindowAverageRequestTimeChart({
  intervals,
  loading,
  dimensions: controlledDimensions,
  selectedMetric: controlledSelectedMetric,
  onStateChange,
}: {
  intervals: WorkloadMetricsInterval[];
  loading: boolean;
  dimensions?: GroupingDimension[];
  selectedMetric?: RequestLatencyMetric;
  onStateChange?: (dimensions: GroupingDimension[], metric: RequestLatencyMetric) => void;
}) {
  const processingLoadTooltipId = useId();
  const tableRef = useRef<HTMLTableElement>(null);
  const previousMetricPositions = useRef<Partial<Record<RequestLatencyMetric, number>>>({});
  const [internalDimensions, setInternalDimensions] = useState<GroupingDimension[]>(
    () => [...DEFAULT_WORKLOAD_INSIGHT_STATE.latencyDimensions],
  );
  const [internalSelectedMetric, setInternalSelectedMetric] = useState<RequestLatencyMetric>(
    DEFAULT_WORKLOAD_INSIGHT_STATE.latencyMetric,
  );
  const dimensions = controlledDimensions ?? internalDimensions;
  const selectedMetric = controlledSelectedMetric ?? internalSelectedMetric;
  const summaries = useMemo(
    () => buildWorkloadLatencySummaries(intervals, dimensions),
    [dimensions, intervals],
  );
  const orderedMetrics = [
    ...REQUEST_LATENCY_METRICS.filter((metric) => metric.key !== selectedMetric),
    REQUEST_LATENCY_METRICS.find((metric) => metric.key === selectedMetric)!,
  ];
  const selectedMetricLabel = orderedMetrics.at(-1)!.label;
  const sortedSummaries = [...summaries].sort((a, b) => (
    (metricValue(b, selectedMetric) ?? Number.NEGATIVE_INFINITY)
    - (metricValue(a, selectedMetric) ?? Number.NEGATIVE_INFINITY)
    || a.key.localeCompare(b.key)
  ));
  const maximumMetricValue = Math.max(
    0,
    ...summaries.map((summary) => metricValue(summary, selectedMetric) ?? 0),
  );
  const dimensionLabel = dimensions
    .map((dimension) => GROUPING_DIMENSIONS.find((item) => item.key === dimension)!.label)
    .join(' × ');

  const toggleDimension = (dimension: GroupingDimension) => {
    let nextDimensions: GroupingDimension[];
    if (dimensions.includes(dimension)) {
      nextDimensions = dimensions.length === 1
        ? dimensions
        : dimensions.filter((item) => item !== dimension);
    } else {
      const selected = new Set([...dimensions, dimension]);
      nextDimensions = AVAILABLE_DIMENSIONS
        .map((item) => item.key)
        .filter((key) => selected.has(key));
    }
    if (controlledDimensions === undefined) setInternalDimensions(nextDimensions);
    onStateChange?.(nextDimensions, selectedMetric);
  };

  const selectMetric = (metric: RequestLatencyMetric) => {
    if (metric === selectedMetric) return;
    const table = tableRef.current;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (table && !reduceMotion) {
      previousMetricPositions.current = Object.fromEntries(
        REQUEST_LATENCY_METRICS.map(({ key }) => [
          key,
          table.querySelector<HTMLElement>(`[data-metric-column="${key}"]`)
            ?.getBoundingClientRect().left,
        ]).filter((entry): entry is [RequestLatencyMetric, number] => entry[1] !== undefined),
      );
    }
    if (controlledSelectedMetric === undefined) setInternalSelectedMetric(metric);
    onStateChange?.(dimensions, metric);
  };

  useLayoutEffect(() => {
    const table = tableRef.current;
    const oldPositions = previousMetricPositions.current;
    previousMetricPositions.current = {};
    if (!table) return;

    for (const { key } of REQUEST_LATENCY_METRICS) {
      const oldLeft = oldPositions[key];
      const newLeft = table.querySelector<HTMLElement>(`[data-metric-column="${key}"]`)
        ?.getBoundingClientRect().left;
      if (oldLeft === undefined || newLeft === undefined) continue;
      const horizontalOffset = oldLeft - newLeft;
      if (Math.abs(horizontalOffset) < 1) continue;
      table.querySelectorAll<HTMLElement>(`[data-metric-column="${key}"]`).forEach((cell) => {
        cell.animate?.(
          [
            { transform: `translateX(${horizontalOffset}px)` },
            { transform: 'translateX(0)' },
          ],
          { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
        );
      });
    }
  }, [selectedMetric]);

  return (
    <section
      aria-label="Request latency table"
      className="space-y-2 rounded-xl border border-primary/20 bg-muted/40 p-2.5 shadow-sm"
    >
      <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Request latency grouping">
        <span className="mr-1 text-xs text-muted-foreground">Group by</span>
        {AVAILABLE_DIMENSIONS.map((dimension) => {
          const selected = dimensions.includes(dimension.key);
          return (
            <button
              key={dimension.key}
              type="button"
              aria-pressed={selected}
              onClick={() => toggleDimension(dimension.key)}
              className={cn(
                'inline-flex h-6 items-center rounded-md border px-2.5 text-xs transition-colors',
                selected
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'bg-background text-muted-foreground hover:bg-muted',
              )}
            >
              {dimension.label}
            </button>
          );
        })}
      </div>

      <div className="min-w-0 rounded-lg border bg-card px-3 pt-3 pb-1 shadow-sm">
        <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <h3 className="text-sm font-semibold">Request latency by {dimensionLabel}</h3>
          <p className="text-xs text-muted-foreground">
            Request-weighted totals and mean; percentiles and slow shares use summed latency histograms. The ≥5 s bucket uses its lower bound.
          </p>
        </div>
        <MetricsLoadingOverlay loading={loading}>
          {summaries.length === 0 && !loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No workload metrics were recorded in this window.</div>
          ) : (
            <div className="max-h-[720px] overflow-auto pb-2">
              <table
                ref={tableRef}
                aria-label="Request latency by workload"
                className="w-full min-w-[1420px] table-fixed border-separate border-spacing-0 text-xs"
              >
                <colgroup>
                  <col className="w-[13%]" />
                  <col className="w-[8%]" />
                  <col className="w-[13%]" />
                  {REQUEST_LATENCY_METRICS.map((metric) => <col key={metric.key} className="w-[7%]" />)}
                  <col className="w-[10%]" />
                </colgroup>
                <thead className="sticky top-0 z-10 bg-card text-muted-foreground">
                  <tr>
                    <th scope="col" className="border-b px-2 py-2 text-left font-medium">ResourceType</th>
                    <th scope="col" className="border-b px-2 py-2 text-left font-medium">Action</th>
                    <th scope="col" className="border-b px-2 py-2 text-left font-medium">APILabel</th>
                    {orderedMetrics.map((metric) => {
                      const selected = metric.key === selectedMetric;
                      return (
                        <th
                          key={metric.key}
                          scope="col"
                          data-metric-column={metric.key}
                          aria-label={metric.label}
                          aria-sort={selected ? 'descending' : 'none'}
                          className={cn(
                            'relative border-b bg-card px-2 py-2 text-right font-medium',
                            selected && 'z-[1] bg-primary/5 text-foreground',
                          )}
                        >
                          <span className="inline-flex items-center justify-end gap-1">
                            <button
                              type="button"
                              aria-label={`Sort by ${metric.label}`}
                              aria-pressed={selected}
                              onClick={() => selectMetric(metric.key)}
                              className="inline-flex items-center gap-0.5 rounded-sm outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              {metric.label}
                              {selected && <ArrowDown className="h-3 w-3" aria-hidden="true" />}
                            </button>
                            {metric.key === 'processingSharePercent' && (
                              <span className="group relative inline-flex">
                                <button
                                  type="button"
                                  aria-label="Processing load explanation"
                                  aria-describedby={processingLoadTooltipId}
                                  className="cursor-help rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                >
                                  <CircleQuestionMark className="h-3.5 w-3.5" aria-hidden="true" />
                                </button>
                                <span
                                  id={processingLoadTooltipId}
                                  role="tooltip"
                                  className="pointer-events-none invisible absolute top-full right-0 z-50 mt-1.5 w-72 rounded-md border border-slate-700 bg-slate-950 px-2.5 py-2 text-left text-xs font-normal text-white opacity-0 shadow-lg transition-opacity duration-75 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
                                >
                                  How big a slice of the server&apos;s total work this bucket accounts for. Technically: this bucket&apos;s total processing time divided by total processing time across all buckets in the selected window.
                                </span>
                              </span>
                            )}
                          </span>
                        </th>
                      );
                    })}
                    <th scope="col" className="border-b px-2 py-2 text-left font-medium">Relative {selectedMetricLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSummaries.map((summary) => {
                    const selectedValue = metricValue(summary, selectedMetric);
                    const formattedMetric = formatMetricValue(summary, selectedMetric);
                    const width = selectedValue === null || maximumMetricValue === 0
                      ? 0
                      : selectedValue / maximumMetricValue * 100;
                    return (
                      <tr key={summary.key}>
                        <td title={summary.groupValues.resourceType} className="truncate border-b px-2 py-1.5 font-mono">{summary.groupValues.resourceType ?? 'All'}</td>
                        <td title={summary.groupValues.action} className="truncate border-b px-2 py-1.5 font-mono">{summary.groupValues.action ?? 'All'}</td>
                        <td title={summary.groupValues.apiLabel} className="truncate border-b px-2 py-1.5 font-mono">{summary.groupValues.apiLabel ?? 'All'}</td>
                        {orderedMetrics.map((metric) => (
                          <td
                            key={metric.key}
                            data-metric-column={metric.key}
                            title={metric.key === 'processingSharePercent'
                              ? `${summary.totalProcessingTimeNs.toLocaleString()} ns of total processing time`
                              : metric.key === 'slowerThan250Percent'
                                ? `${summary.slowerThan250Count.toLocaleString()} of ${summary.measuredLatencyRequests.toLocaleString()} histogram requests`
                                : metric.key === 'slowerThan1000Percent'
                                  ? `${summary.slowerThan1000Count.toLocaleString()} of ${summary.measuredLatencyRequests.toLocaleString()} histogram requests`
                                  : metric.key.startsWith('histogram')
                                    ? `${summary.measuredLatencyRequests.toLocaleString()} histogram requests`
                                    : undefined}
                            className={cn(
                              'relative whitespace-nowrap border-b bg-card px-2 py-1.5 text-right font-mono tabular-nums',
                              metric.key === selectedMetric && 'z-[1] bg-primary/5',
                            )}
                          >
                            {formatMetricValue(summary, metric.key)}
                          </td>
                        ))}
                        <td className="border-b px-2 py-1.5">
                          <div
                            role="img"
                            aria-label={`${summary.label}: ${selectedMetricLabel} ${formattedMetric}`}
                            title={`${summary.label}: ${selectedMetricLabel} ${formattedMetric} across ${summary.totalRequests.toLocaleString()} requests`}
                            className="h-4 min-w-px rounded-sm bg-sky-600"
                            style={{ width: `${width}%` }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </MetricsLoadingOverlay>
      </div>
    </section>
  );
}
